import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Save, Plus, Trash2, Clock } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import LoadingSpinner from '../../components/LoadingSpinner';
import * as vendorApi from '../../api/vendor.api';
import { errorMessage } from '../../api/client';

// Client 2026-08-04: clinic-side page to configure availability.
// Working days (checkboxes Sun–Sat), day-wide open hours, per-slot
// duration, and one or more break windows. Feeds the patient-facing
// slot picker (see /clinics/:id/available-slots).

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function BookingSettings() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [saving, setSaving]     = useState(false);
  const [err, setErr]           = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await vendorApi.getBookingSettings();
        if (!cancelled) setSettings(data);
      } catch (e) {
        if (!cancelled) setErr(errorMessage(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  function toggleDay(d) {
    setSettings((s) => {
      const set = new Set(s.workingDays || []);
      if (set.has(d)) set.delete(d); else set.add(d);
      return { ...s, workingDays: Array.from(set).sort((a, b) => a - b) };
    });
  }

  function addBreak() {
    setSettings((s) => ({
      ...s,
      breaks: [...(s.breaks || []), { start: '13:00', end: '14:00', label: '' }],
    }));
  }

  function updateBreak(idx, patch) {
    setSettings((s) => ({
      ...s,
      breaks: s.breaks.map((b, i) => (i === idx ? { ...b, ...patch } : b)),
    }));
  }

  function removeBreak(idx) {
    setSettings((s) => ({ ...s, breaks: s.breaks.filter((_, i) => i !== idx) }));
  }

  async function onSave(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const saved = await vendorApi.updateBookingSettings(settings);
      setSettings(saved);
      toast.success('Booking settings saved');
    } catch (e1) {
      toast.error(errorMessage(e1));
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <LoadingSpinner size="lg" />;
  if (err)      return <div className="notice warning">{err}</div>;
  if (!settings) return null;

  return (
    <>
      <PageHeader
        title="Booking settings"
        subtitle="Choose which days you're open, your working hours, slot length, and any break times. Patients will only see slots that respect these rules."
      />

      <form onSubmit={onSave} className="card" style={{ maxWidth: 720 }}>
        {/* Working days */}
        <div className="field">
          <label>Working days</label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {DAY_NAMES.map((name, i) => {
              const active = (settings.workingDays || []).includes(i);
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => toggleDay(i)}
                  className={active ? 'btn btn-primary' : 'btn btn-outline'}
                  style={{ minWidth: 64 }}
                >
                  {name}
                </button>
              );
            })}
          </div>
          <div className="text-xs muted" style={{ marginTop: 6 }}>
            Un-selected days are treated as closed (holidays).
          </div>
        </div>

        {/* Working hours */}
        <div className="grid grid-2">
          <div className="field">
            <label>Open time</label>
            <input
              type="time"
              className="input"
              value={settings.startTime || '09:00'}
              onChange={(e) => setSettings({ ...settings, startTime: e.target.value })}
              required
            />
          </div>
          <div className="field">
            <label>Close time</label>
            <input
              type="time"
              className="input"
              value={settings.endTime || '18:00'}
              onChange={(e) => setSettings({ ...settings, endTime: e.target.value })}
              required
            />
          </div>
        </div>

        {/* Slot duration + horizon */}
        <div className="grid grid-2">
          <div className="field">
            <label>Slot length (minutes)</label>
            <input
              type="number"
              min={5} max={240} step={5}
              className="input"
              value={settings.slotDurationMin || 30}
              onChange={(e) => setSettings({ ...settings, slotDurationMin: Number(e.target.value) })}
              required
            />
            <div className="text-xs muted" style={{ marginTop: 4 }}>
              How long each patient appointment is.
            </div>
          </div>
          <div className="field">
            <label>Booking horizon (days)</label>
            <input
              type="number"
              min={1} max={180}
              className="input"
              value={settings.bookingHorizonDays || 30}
              onChange={(e) => setSettings({ ...settings, bookingHorizonDays: Number(e.target.value) })}
              required
            />
            <div className="text-xs muted" style={{ marginTop: 4 }}>
              How far ahead patients can book.
            </div>
          </div>
        </div>

        {/* Breaks */}
        <div className="field">
          <label>
            <Clock size={14} style={{ verticalAlign: -2, marginRight: 6 }} />
            Break windows (no bookings during these)
          </label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {(settings.breaks || []).map((b, i) => (
              <div key={i} className="row" style={{ gap: 8, alignItems: 'center' }}>
                <input
                  type="time"
                  className="input"
                  value={b.start}
                  onChange={(e) => updateBreak(i, { start: e.target.value })}
                  required
                  style={{ maxWidth: 130 }}
                />
                <span className="muted">to</span>
                <input
                  type="time"
                  className="input"
                  value={b.end}
                  onChange={(e) => updateBreak(i, { end: e.target.value })}
                  required
                  style={{ maxWidth: 130 }}
                />
                <input
                  type="text"
                  className="input"
                  placeholder="Label (e.g. Lunch)"
                  value={b.label || ''}
                  onChange={(e) => updateBreak(i, { label: e.target.value })}
                  style={{ flex: 1 }}
                />
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => removeBreak(i)}
                  aria-label="Remove break"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
            <button type="button" className="btn btn-outline" onClick={addBreak} style={{ alignSelf: 'flex-start' }}>
              <Plus size={14} /> Add break
            </button>
          </div>
        </div>

        <div className="row" style={{ marginTop: 'var(--s-4)' }}>
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? <span className="spinner" /> : <Save size={16} />}
            {saving ? 'Saving…' : 'Save settings'}
          </button>
        </div>
      </form>
    </>
  );
}

export default BookingSettings;
